import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageIntro } from "@/components/PageIntro";
import { ShieldCheck, RotateCw, FileSpreadsheet, Search, Pencil, Trash2, CloudDownload } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { MparivahanScan } from "@/components/MparivahanScan";

const ITEMS = [
  { k: "insurance_expiry", l: "Insurance" },
  { k: "tax_expiry", l: "Road Tax" },
  { k: "fitness_expiry", l: "Fitness" },
  { k: "puc_expiry", l: "PUC" },
  { k: "permit_expiry", l: "Permit" },
  { k: "national_permit_expiry", l: "Nat. Permit" },
];

const STATUS = {
  valid: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  soon: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  critical: "bg-orange-500/15 text-orange-600 border-orange-500/40",
  expired: "bg-rose-500/10 text-rose-600 border-rose-500/30",
  unset: "bg-slate-500/10 text-slate-500 border-slate-500/20",
};

const emptyForm = () => ({ vehicle_number: "", insurance_expiry: "", tax_expiry: "", fitness_expiry: "", puc_expiry: "", permit_expiry: "", national_permit_expiry: "", insurance_provider: "", notes: "" });

export default function Compliance() {
  const { selectedHub, hubs } = useApp();
  const hubName = hubs.find((h) => h.id === selectedHub)?.name || "";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [importing, setImporting] = useState(false);
  const fileRef = React.useRef(null);

  const load = useCallback(async () => {
    if (!selectedHub || selectedHub === "all") { setRows([]); setSummary(null); return; }
    const [list, sum] = await Promise.all([
      api.get("/compliance", { params: { hub: selectedHub } }),
      api.get("/compliance/summary", { params: { hub: selectedHub } }),
    ]);
    setRows(list.data); setSummary(sum.data);
  }, [selectedHub]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (r) => {
    setEditing(r.vehicle_number);
    setForm({ ...emptyForm(), ...Object.fromEntries(ITEMS.map((i) => [i.k, r[i.k] || ""])), vehicle_number: r.vehicle_number, insurance_provider: r.insurance_provider || "", notes: r.notes || "" });
  };
  const save = async () => {
    await api.put(`/compliance/${form.vehicle_number}`, form, { params: { hub: selectedHub } });
    toast.success(`Compliance saved for ${form.vehicle_number}`);
    setEditing(null); load();
  };
  const clearRec = async (r) => {
    if (!r.id) return;
    await api.delete(`/compliance/${r.id}`);
    toast.success(`Compliance cleared for ${r.vehicle_number}`);
    load();
  };
  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("hub", selectedHub);
      const { data } = await api.post("/compliance/import", fd);
      toast.success(`Imported ${data.created} new · ${data.updated} updated${data.errors?.length ? ` · ${data.errors.length} row error(s)` : ""}`);
      if (data.errors?.length) console.warn("Compliance import issues:", data.errors);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    }
    setImporting(false);
  };
  const syncVahan = async () => {
    try {
      await api.post("/compliance/sync-vahan", {});
      toast.success("VAHAN sync complete");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "VAHAN sync unavailable");
    }
  };

  const filtered = rows.filter((r) => !q || r.vehicle_number.includes(q.toUpperCase()) || (r.owner_name || "").toLowerCase().includes(q.toLowerCase()));
  const daysLeft = (d) => (d ? Math.ceil((new Date(d) - Date.now()) / 86400000) : null);

  return (
    <div className="space-y-5">
      <PageIntro title="Vehicle Compliance" subtitle={`Insurance · Road Tax · Fitness · PUC · Permit tracking — ${hubName}`} />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="compliance-summary">
          {[
            { l: "Valid", v: summary.valid, c: "text-emerald-500" },
            { l: "Expiring ≤30d", v: summary.soon, c: "text-amber-500" },
            { l: "Critical ≤15d", v: summary.critical, c: "text-orange-500" },
            { l: "Expired", v: summary.expired, c: "text-rose-500" },
            { l: "Not Set", v: summary.unset, c: "text-slate-400" },
          ].map((c) => (
            <div key={c.l} className="bg-card border border-border rounded-xl p-4 shadow-sm" data-testid={`compliance-card-${c.l.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.l}</div>
              <div className={`font-display text-2xl font-bold mt-1 ${c.c}`}>{c.v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search truck / owner…" className="pl-9" data-testid="compliance-search" />
        </div>
        <Button variant="outline" onClick={load} data-testid="compliance-refresh"><RotateCw className="h-4 w-4 mr-1.5" />Refresh</Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="compliance-import"><FileSpreadsheet className="h-4 w-4 mr-1.5" />{importing ? "Importing…" : "Import Excel"}</Button>
        {selectedHub && selectedHub !== "all" && <MparivahanScan hub={selectedHub} rows={rows} onDone={load} />}
        <Button variant="outline" onClick={syncVahan} data-testid="compliance-vahan-sync" title="Fetch latest from VAHAN (needs provider API key)"><CloudDownload className="h-4 w-4 mr-1.5" />Sync VAHAN</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={importFile} data-testid="compliance-import-file" />
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap" data-testid="compliance-table">
          <thead className="bg-muted/60 text-muted-foreground text-xs uppercase">
            <tr>
              <th className="py-3 px-4 text-left">Truck</th>
              <th className="py-3 px-4 text-left">Owner</th>
              {ITEMS.map((i) => <th key={i.k} className="py-3 px-3 text-left">{i.l}</th>)}
              <th className="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.vehicle_number} className="border-t border-border hover:bg-accent/40" data-testid={`compliance-row-${r.vehicle_number}`}>
                <td className="py-2.5 px-4 font-mono font-medium">{r.vehicle_number}</td>
                <td className="py-2.5 px-4 max-w-[160px] truncate">{r.owner_name || "-"}</td>
                {ITEMS.map((i) => {
                  const pill = STATUS[r[`${i.k}_status`] || "unset"];
                  const d = r[i.k];
                  const dd = daysLeft(d);
                  return (
                    <td key={i.k} className="py-2.5 px-3">
                      <span className={`inline-flex flex-col px-2 py-1 rounded-lg border text-xs ${pill}`} data-testid={`compliance-${i.k}-${r.vehicle_number}`}>
                        <span className="font-mono font-semibold">{d ? fmtDate(d) : "—"}</span>
                        <span className="text-[10px] opacity-80">{d ? (dd < 0 ? `expired ${-dd}d ago` : `${dd}d left`) : "Not set"}</span>
                      </span>
                    </td>
                  );
                })}
                <td className="py-2.5 px-4">
                  <div className="flex items-center justify-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1.5 font-medium" onClick={() => openEdit(r)} data-testid={`compliance-edit-${r.vehicle_number}`} title="Edit compliance dates"><Pencil className="h-3.5 w-3.5" />Edit</Button>
                    {r.id && <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-500 hover:bg-rose-500/10" onClick={() => clearRec(r)} data-testid={`compliance-clear-${r.vehicle_number}`} title="Clear compliance record"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="py-16 text-center text-muted-foreground">No vehicles found for this hub</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg" data-testid="compliance-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Compliance — {form.vehicle_number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {ITEMS.map((i) => (
              <label key={i.k} className="text-xs text-muted-foreground space-y-1">{i.l} Expiry
                <Input type="date" value={form[i.k]} onChange={(e) => setForm((p) => ({ ...p, [i.k]: e.target.value }))} data-testid={`compliance-form-${i.k}`} />
              </label>
            ))}
            <label className="text-xs text-muted-foreground space-y-1">Insurance Provider
              <Input value={form.insurance_provider} onChange={(e) => setForm((p) => ({ ...p, insurance_provider: e.target.value }))} data-testid="compliance-form-insurer" />
            </label>
            <label className="text-xs text-muted-foreground space-y-1">Notes
              <Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="compliance-form-notes" />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="compliance-cancel">Cancel</Button>
            <Button onClick={save} data-testid="compliance-save"><ShieldCheck className="h-4 w-4 mr-1.5" />Save Compliance</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
